import React from 'react';
import { Check } from 'lucide-react';
import { seededWalk, toLineD } from './mockChart';

const BULLETS = [
  'Overlay any two assets or more on one normalised chart',
  'Benchmark crypto against popular stocks and ETFs',
  'Spot divergence and correlation at a glance',
];

const BTC_COLOR    = '#f7931a';
const NASDAQ_COLOR = '#6b95cf';
const GREEN        = '#6db981';
const RED          = '#c4615e';

// % return walks in a 0..240 box: 0% at y=120, 2px per %, so +60% = y0, -60% = y240.
// Real Jul-2025 → Jul-2026: BTC falls to ~-46% (y=212), Nasdaq rises to ~+20% (y=80).
const BTC_D    = toLineD(seededWalk({ seed: 7,  points: 42, width: 800, yStart: 120, yEnd: 212, vol: 11, band: 12 }));
const NASDAQ_D = toLineD(seededWalk({ seed: 21, points: 42, width: 800, yStart: 120, yEnd: 80,  vol: 5,  band: 10 }));

const Y_TICKS = ['+60%', '+30%', '0%', '-30%', '-60%'];
const X_TICKS = ['Aug', 'Nov', 'Feb', 'May', 'Jul'];

// Comparison summary (static, matches real 1Y performance). Nasdaq is the winner.
const SUMMARY: {
  name: string; symbol: string; color: string;
  start: string; end: string; ret: string; winner: boolean;
}[] = [
  { name: 'Bitcoin',    symbol: 'BTC', color: BTC_COLOR,    start: 'A$118,003.30', end: 'A$63,508.70', ret: '-46.18%', winner: false },
  { name: 'Nasdaq 100', symbol: 'QQQ', color: NASDAQ_COLOR, start: 'A$568.14',     end: 'A$682.12',    ret: '+20.06%', winner: true  },
];

function SummaryRow({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[10px]" style={{ color: 'rgba(244,241,234,0.45)' }}>{label}</span>
      <span className="text-[11px] font-medium tabular-nums" style={{ color }}>{value}</span>
    </div>
  );
}

export function ComparisonSection() {
  return (
    <section
      className="px-6 py-24 relative"
      style={{
        borderTop: '0.5px solid rgba(255,255,255,0.06)',
        background:
          'linear-gradient(180deg, rgba(107,149,207,0.05), transparent 60%)',
      }}
    >
      {/* Text is first in the DOM (so mobile reads copy then mock), but on desktop
          the mock sits on the LEFT and the copy on the RIGHT for visual rhythm. */}
      <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-5 gap-10 md:gap-12 items-center">
        {/* COPY - 2/5, right on desktop */}
        <div className="md:col-span-2 md:order-2">
          <div
            className="text-[11px] font-semibold tracking-[0.18em] mb-4"
            style={{ color: '#d4b483' }}
          >
            CRYPTO + STOCKS
          </div>
          <h2
            className="font-semibold"
            style={{
              color: '#f4f1ea',
              fontSize: 'clamp(26px, 4vw, 36px)',
              letterSpacing: '-0.02em',
              lineHeight: 1.15,
            }}
          >
            Compare crypto against stocks.
          </h2>
          <p
            className="mt-4 text-[15px] leading-relaxed"
            style={{ color: 'rgba(244,241,234,0.6)' }}
          >
            Put Bitcoin head-to-head with the Nasdaq, or any of the stocks and ETFs we
            cover, on a single normalised chart, and see how your assets really stack up.
          </p>

          <ul className="mt-6 space-y-3">
            {BULLETS.map((b) => (
              <li key={b} className="flex items-start gap-3">
                <span
                  className="flex items-center justify-center rounded-full mt-0.5"
                  style={{
                    width: 18,
                    height: 18,
                    backgroundColor: 'rgba(212,180,131,0.15)',
                  }}
                >
                  <Check className="w-3 h-3" style={{ color: '#d4b483' }} strokeWidth={3} />
                </span>
                <span className="text-[14px]" style={{ color: 'rgba(244,241,234,0.8)' }}>
                  {b}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* MOCK - 3/5, left on desktop */}
        <div className="md:col-span-3 md:order-1">
          <div
            className="rounded-xl"
            style={{
              backgroundColor: '#131313',
              border: '0.5px solid rgba(255,255,255,0.08)',
              padding: '18px',
            }}
          >
            {/* Header: title + legend */}
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div
                className="text-[10px] tracking-[0.14em]"
                style={{ color: 'rgba(244,241,234,0.45)' }}
              >
                ASSET COMPARISON · JUL 2025 – JUL 2026
              </div>
              <div className="flex items-center gap-4 text-[11px]">
                <span className="inline-flex items-center gap-1.5" style={{ color: 'rgba(244,241,234,0.7)' }}>
                  <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: BTC_COLOR }} />
                  Bitcoin (BTC)
                </span>
                <span className="inline-flex items-center gap-1.5" style={{ color: 'rgba(244,241,234,0.7)' }}>
                  <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: NASDAQ_COLOR }} />
                  Nasdaq 100 (QQQ)
                </span>
              </div>
            </div>

            {/* Chart with % y-axis + date x-axis */}
            <div className="flex gap-2">
              <div
                className="flex flex-col justify-between text-[9px] tabular-nums py-0.5 flex-shrink-0"
                style={{ color: 'rgba(244,241,234,0.35)' }}
              >
                {Y_TICKS.map((t) => <span key={t}>{t}</span>)}
              </div>
              <div className="flex-1">
                <svg
                  viewBox="0 0 800 240"
                  preserveAspectRatio="none"
                  className="w-full h-[180px] sm:h-[210px]"
                >
                  {/* gridlines: +60 / +30 / 0 (brighter) / -30 / -60 */}
                  <line x1="0" y1="0"   x2="800" y2="0"   stroke="rgba(255,255,255,0.04)" />
                  <line x1="0" y1="60"  x2="800" y2="60"  stroke="rgba(255,255,255,0.04)" />
                  <line x1="0" y1="120" x2="800" y2="120" stroke="rgba(255,255,255,0.10)" />
                  <line x1="0" y1="180" x2="800" y2="180" stroke="rgba(255,255,255,0.04)" />
                  <line x1="0" y1="240" x2="800" y2="240" stroke="rgba(255,255,255,0.04)" />

                  <path
                    d={NASDAQ_D}
                    fill="none"
                    stroke={NASDAQ_COLOR}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d={BTC_D}
                    fill="none"
                    stroke={BTC_COLOR}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div
                  className="flex justify-between text-[9px] mt-1"
                  style={{ color: 'rgba(244,241,234,0.35)' }}
                >
                  {X_TICKS.map((t) => <span key={t}>{t}</span>)}
                </div>
              </div>
            </div>

            {/* Winner summary cards */}
            <div className="grid grid-cols-2 gap-2.5 mt-4">
              {SUMMARY.map((s) => (
                <div
                  key={s.symbol}
                  className="rounded-lg p-3"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.02)',
                    border: s.winner
                      ? '0.5px solid rgba(212,180,131,0.35)'
                      : '0.5px solid rgba(255,255,255,0.05)',
                  }}
                >
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <span className="inline-flex items-center gap-1.5 min-w-0">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: s.color }} />
                      <span className="text-[11px] font-medium truncate" style={{ color: '#f4f1ea' }}>
                        {s.name} <span style={{ color: 'rgba(244,241,234,0.4)' }}>{s.symbol}</span>
                      </span>
                    </span>
                    {s.winner && (
                      <span
                        className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: '#d4b483', color: '#0c0e12' }}
                      >
                        Winner
                      </span>
                    )}
                  </div>
                  <div className="space-y-1">
                    <SummaryRow label="Start"  value={s.start} color="rgba(244,241,234,0.75)" />
                    <SummaryRow label="End"    value={s.end}   color="rgba(244,241,234,0.75)" />
                    <SummaryRow label="Return" value={s.ret}   color={s.ret.startsWith('-') ? RED : GREEN} />
                  </div>
                </div>
              ))}
            </div>

            <div
              className="mt-3 text-[10px] tracking-wider"
              style={{ color: 'rgba(244,241,234,0.4)' }}
            >
              Illustrative example using historical data.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
